import { useTranslation } from 'react-i18next';
import { LogOut, User, Shield, PanelRightClose, PanelRightOpen, Sun, Moon, HelpCircle, Factory, Check } from 'lucide-react';
import { LanguageSwitcher } from '@/components/shared/LanguageSwitcher';
import { useDisplayMode } from '@/hooks/useDisplayMode';
import { useTheme } from '@/hooks/useTheme';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { useTourStore } from '@/stores/useTourStore';
import { NotificationBell } from '@/components/NotificationBell';
import { PerformancePanel } from '@/components/PerformancePanel';
import { OrgSwitcher } from '@/components/tenant/OrgSwitcher';
import { useChatPanel } from '@/stores/useChatPanel';
import { GlobalSearch } from '@/components/GlobalSearch';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { SidebarTrigger } from '@/components/ui/sidebar';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';

import { useVirtualClock } from '@/stores/useVirtualClock';
import { VirtualClockControl } from '@/components/VirtualClockControl';
import { useAuth } from '@/contexts/AuthContext';

export function TopHeader() {
  const { t, i18n } = useTranslation();
  const virtualTime = useVirtualClock((s) => s.virtualTime);
  const { user, profile, role, signOut } = useAuth();
  const chatOpen = useChatPanel((s) => s.isOpen);
  const toggleChat = useChatPanel((s) => s.toggle);
  const { theme, toggleTheme } = useTheme();
  const online = useOnlineStatus();
  const startTour = useTourStore((s) => s.start);

  const displayName = profile?.full_name || user?.email?.split('@')[0] || 'Usuario';
  const displayRole = role ? t(`roles.${role}`) : t('header.noRole');
  const shift = profile?.shift || 'A';
  const locale = i18n.language === 'en' ? 'en-GB' : 'es-ES';


  return (
    <header className="flex h-12 items-center justify-between border-b bg-card px-4 shrink-0">
      <div className="flex items-center gap-2">
        <SidebarTrigger className="h-8 w-8" aria-label="Mostrar u ocultar menú lateral" />
        <span
          className={
            'inline-block h-2 w-2 rounded-full ' +
            (online ? 'bg-emerald-500 shadow-[0_0_6px_hsl(var(--primary))]' : 'bg-destructive animate-pulse')
          }
          title={online ? 'Conectado' : 'Sin conexión'}
          aria-label={online ? 'Online' : 'Offline'}
        />
        <div data-tour="global-search"><GlobalSearch /></div>
      </div>

      <div className="flex items-center gap-3">
        <LanguageSwitcher />

        <span className="text-xs font-mono text-muted-foreground hidden sm:block">
          {virtualTime.toLocaleDateString(locale, { weekday: 'short', day: '2-digit', month: 'short' })}
        </span>

        <VirtualClockControl />

        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={toggleTheme}
          title={theme === 'dark' ? 'Modo claro' : 'Modo oscuro'}
          aria-label="Toggle theme"
        >
          {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </Button>

        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={startTour}
          title="Iniciar tour"
          aria-label="Iniciar tour"
        >
          <HelpCircle className="h-4 w-4" />
        </Button>

        <div data-tour="notifications"><NotificationBell /></div>
        <PerformancePanel />

        <OrgSwitcher />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-2 rounded-md bg-secondary px-2.5 py-1 hover:bg-accent transition-colors">
              <User className="h-3.5 w-3.5 text-muted-foreground" />
              <div className="hidden sm:block text-left">
                <p className="text-xs font-medium leading-none">{displayName}</p>
                <p className="text-[10px] text-muted-foreground">{displayRole} · {t('header.shift')} {shift}</p>
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <div className="px-2 py-1.5">
              <p className="text-sm font-medium">{displayName}</p>
              <p className="text-xs text-muted-foreground">{user?.email}</p>
              <Badge variant={role === 'admin' ? 'default' : role === 'supervisor' ? 'secondary' : 'outline'} className="mt-1 text-[10px]">
                <Shield className="h-3 w-3 mr-1" /> {displayRole}
              </Badge>
            </div>
            <DropdownMenuSeparator />
            <PlantModeMenuItem />
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={signOut} className="text-destructive focus:text-destructive">
              <LogOut className="h-4 w-4 mr-2" /> {t('header.logout')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <div data-tour="ai-agent">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={toggleChat}
            title={chatOpen ? 'Ocultar agente' : 'Mostrar agente'}
            aria-label={chatOpen ? 'Ocultar agente' : 'Mostrar agente'}
          >
            {chatOpen ? <PanelRightClose className="h-4 w-4" /> : <PanelRightOpen className="h-4 w-4" />}
          </Button>
        </div>
      </div>
    </header>
  );
}

function PlantModeMenuItem() {
  const { mode, setMode } = useDisplayMode();
  const active = mode === 'plant';
  return (
    <DropdownMenuItem
      onClick={(e) => {
        e.preventDefault();
        setMode(active ? 'standard' : 'plant');
      }}
      className="flex items-center justify-between gap-2"
    >
      <span className="flex items-center">
        <Factory className="h-4 w-4 mr-2" /> Modo Planta
      </span>
      {active && <Check className="h-3.5 w-3.5 text-primary" />}
    </DropdownMenuItem>
  );
}
